import React, { useEffect, useMemo, useRef, useState } from 'react';
import fileDownload from 'js-file-download';

import {
  fetchOpcoesDoRelatorioExterno,
  gerarRelatorioExterno,
  type FiltrosDoRelatorioExterno,
  type FormatoRelatorioExterno,
  type ModoDoRelatorioExterno,
  type ModoRelatorioExterno,
  type OficinaExterna,
  type OpcoesDoRelatorioExterno,
  type RegiaoExterna,
} from '../../../api/oficinaExternaApi';
import { mensagemDeErroDoRelatorio } from '../../../api/reportsApi';
import { notify } from '../../../shared/feedback/notify';

// O casco e as classes são as do Relatório de Veículos (at-107), por import — o modal é o mesmo
// desenho, com o cardápio e os filtros deste módulo.
import '../../../components/modal/car-report/CarsReportModal.css';
import '../../../shared/ui/modal-close-button.css';
import '../../../shared/feedback/erro-de-tela.css';

interface Props {
  regioes: RegiaoExterna[];
  oficinas: OficinaExterna[];
  onFechar: () => void;
}

/**
 * Gerar Relatório da Oficina Externa (at-132), no molde do Relatório de Veículos.
 *
 * 🔴 O cardápio vem do SERVIDOR (`GET /oficina-externa/relatorio/opcoes`), inclusive uma coluna
 * por pergunta do checklist. Sem ele a geração fica BLOQUEADA com a explicação (at-60): gerar
 * "com o que sobrou" produziria um arquivo incompleto sem aviso.
 *
 * ⚠️ Montagem condicional no pai (`{aberto && <Modal/>}`), nunca prop `isOpen` (at-65).
 */
const RelatorioOficinaExternaModal: React.FC<Props> = ({ regioes, oficinas, onFechar }) => {
  const [opcoes, setOpcoes] = useState<OpcoesDoRelatorioExterno | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [falhouOCatalogo, setFalhouOCatalogo] = useState(false);

  const [modoId, setModoId] = useState<ModoRelatorioExterno>('VISITAS');
  const [formato, setFormato] = useState<FormatoRelatorioExterno>('CSV');
  const [colunas, setColunas] = useState<string[]>([]);
  const [filtros, setFiltros] = useState<FiltrosDoRelatorioExterno>({ situacao: 'TODOS' });
  const [gerando, setGerando] = useState(false);

  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let ativo = true;
    void (async () => {
      try {
        const catalogo = await fetchOpcoesDoRelatorioExterno();
        if (!ativo) return;
        setOpcoes(catalogo);
        const inicial =
          catalogo.modos.find((m) => m.id === catalogo.modoPadrao) ?? catalogo.modos[0];
        if (inicial) {
          setModoId(inicial.id);
          setColunas(inicial.padrao);
        }
      } catch (erro) {
        if (!ativo) return;
        setFalhouOCatalogo(true);
        notify.errorFrom(
          erro,
          'Não foi possível carregar as opções do relatório. Recarregue a página; se persistir, acione a TI.',
        );
      } finally {
        if (ativo) setCarregando(false);
      }
    })();
    return () => {
      ativo = false;
    };
  }, []);

  // Fecha por clique fora e por Escape.
  useEffect(() => {
    const cliqueFora = (evento: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(evento.target as Node)) onFechar();
    };
    const tecla = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') onFechar();
    };
    document.addEventListener('mousedown', cliqueFora);
    document.addEventListener('keydown', tecla);
    return () => {
      document.removeEventListener('mousedown', cliqueFora);
      document.removeEventListener('keydown', tecla);
    };
  }, [onFechar]);

  const modo: ModoDoRelatorioExterno | null = useMemo(
    () => opcoes?.modos.find((m) => m.id === modoId) ?? null,
    [opcoes, modoId],
  );

  const secoes = useMemo(() => {
    const mapa = new Map<string, ModoDoRelatorioExterno['colunas']>();
    for (const coluna of modo?.colunas ?? []) {
      mapa.set(coluna.secao, [...(mapa.get(coluna.secao) ?? []), coluna]);
    }
    return Array.from(mapa.entries()).map(([nome, itens]) => ({ nome, itens }));
  }, [modo]);

  const oficinasDaRegiao = filtros.regiaoId
    ? oficinas.filter((o) => (o.regiaoId ?? o.regiao?.id) === filtros.regiaoId)
    : oficinas;

  const trocarModo = (novo: ModoRelatorioExterno) => {
    setModoId(novo);
    // Cada modo tem colunas próprias: manter a seleção deixaria marcadas colunas que o servidor
    // descartaria em silêncio.
    setColunas(opcoes?.modos.find((m) => m.id === novo)?.padrao ?? []);
  };

  const alternar = (id: string) =>
    setColunas((atuais) => (atuais.includes(id) ? atuais.filter((c) => c !== id) : [...atuais, id]));

  const marcarTodos = () => {
    if (!modo) return;
    const todas = modo.colunas.map((c) => c.id);
    setColunas(colunas.length === todas.length ? [] : todas);
  };

  const filtro = (chave: keyof FiltrosDoRelatorioExterno, valor: string) =>
    setFiltros((atuais) => ({ ...atuais, [chave]: valor }));

  const excedeOPdf = formato === 'PDF' && !!opcoes && colunas.length > opcoes.maxColunasPdf;
  const podeGerar = !!modo && colunas.length > 0 && !falhouOCatalogo && !excedeOPdf && !gerando;

  const gerar = async () => {
    if (!modo) return;
    setGerando(true);
    try {
      const { arquivo, nome } = await gerarRelatorioExterno({
        colunas,
        modo: modo.id,
        formato,
        filtros,
      });
      fileDownload(arquivo, nome);
      notify.success('Relatório gerado. O download foi iniciado.');
      onFechar();
    } catch (erro) {
      // A resposta é blob: a mensagem do servidor só chega pelo helper do Relatório de Veículos.
      const doServidor = await mensagemDeErroDoRelatorio(erro);
      notify.error(
        doServidor ?? 'Não foi possível gerar o relatório. Tente novamente; se persistir, acione a TI.',
      );
    } finally {
      setGerando(false);
    }
  };

  return (
    <div className="cars-report-modal-overlay">
      <div className="cars-report-modal-content" ref={modalRef}>
        <div className="cars-report-modal-header">
          <h2>Gerar Relatório da Oficina Externa</h2>
          <button className="close-button" onClick={onFechar} aria-label="Fechar modal">
            &times;
          </button>
        </div>

        {carregando ? (
          <div className="cars-report-modal-loading">
            <p>Carregando as opções do relatório...</p>
          </div>
        ) : (
          <>
            <div className="cars-report-modal-body">
              {falhouOCatalogo && (
                <div className="erro-de-tela">
                  Não foi possível carregar as opções do relatório, então a geração está
                  bloqueada. Sem a lista de colunas, o arquivo sairia incompleto sem aviso.
                  Recarregue a página; se persistir, acione a TI.
                </div>
              )}

              <div className="report-section">
                <div className="section-header">
                  <h3>O que este relatório traz</h3>
                </div>
                <div className="report-modo-grid">
                  {(opcoes?.modos ?? []).map((item) => (
                    <button
                      type="button"
                      key={item.id}
                      className={`report-modo-card ${item.id === modoId ? 'active' : ''}`}
                      onClick={() => trocarModo(item.id)}
                    >
                      <span className="report-modo-titulo">{item.titulo}</span>
                      <span className="report-modo-granularidade">{item.granularidade}</span>
                      <span className="report-modo-descricao">{item.descricao}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="report-section">
                <div className="section-header">
                  <h3>Formato do arquivo</h3>
                  <span className="section-subtitle">
                    O PDF sai com a identidade visual da Atual; o CSV abre no Excel
                  </span>
                </div>
                <div className="report-formato-linha">
                  {(['CSV', 'PDF'] as FormatoRelatorioExterno[]).map((opcao) => (
                    <label key={opcao} className={`checkbox-label ${formato === opcao ? 'active' : ''}`}>
                      <input
                        type="radio"
                        name="formato"
                        value={opcao}
                        checked={formato === opcao}
                        onChange={() => setFormato(opcao)}
                      />
                      <span>{opcao === 'CSV' ? 'CSV (planilha)' : 'PDF (para imprimir)'}</span>
                    </label>
                  ))}
                </div>
                {excedeOPdf && (
                  <p className="report-aviso">
                    O PDF comporta até <strong>{opcoes?.maxColunasPdf}</strong> colunas e você
                    escolheu <strong>{colunas.length}</strong>. Desmarque colunas ou gere em CSV,
                    que não tem esse limite.
                  </p>
                )}
              </div>

              <div className="report-section">
                <div className="section-header">
                  <h3>Colunas no Relatório</h3>
                  <button type="button" className="btn-select-all" onClick={marcarTodos}>
                    {modo && colunas.length === modo.colunas.length ? 'Desmarcar Todos' : 'Selecionar Todos'}
                  </button>
                </div>
                {secoes.map((secao) => (
                  <div key={secao.nome} className="report-coluna-secao">
                    <h4 className="report-coluna-secao-titulo">{secao.nome}</h4>
                    <div className="checkbox-grid">
                      {secao.itens.map((coluna) => (
                        <label
                          key={coluna.id}
                          className={`checkbox-label ${colunas.includes(coluna.id) ? 'active' : ''}`}
                        >
                          <input
                            type="checkbox"
                            value={coluna.id}
                            checked={colunas.includes(coluna.id)}
                            onChange={() => alternar(coluna.id)}
                          />
                          <span>{coluna.titulo}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="report-section">
                <div className="section-header">
                  <h3>Filtros</h3>
                </div>
                <div className="filters-grid">
                  <div className="form-field">
                    <label htmlFor="rel-oe-regiao">Região</label>
                    <select
                      id="rel-oe-regiao"
                      value={filtros.regiaoId ?? ''}
                      onChange={(e) =>
                        // Trocar a região limpa a oficina: oficina de outra região zeraria o
                        // relatório sem explicação.
                        setFiltros((atuais) => ({ ...atuais, regiaoId: e.target.value, oficinaId: '' }))
                      }
                    >
                      <option value="">Todas</option>
                      {regioes.map((r) => (
                        <option key={r.id} value={r.id}>
                          {r.nome}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-field">
                    <label htmlFor="rel-oe-oficina">Oficina</label>
                    <select
                      id="rel-oe-oficina"
                      value={filtros.oficinaId ?? ''}
                      onChange={(e) => filtro('oficinaId', e.target.value)}
                    >
                      <option value="">Todas</option>
                      {oficinasDaRegiao.map((o) => (
                        <option key={o.id} value={o.id}>
                          {o.nome}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-field">
                    <label htmlFor="rel-oe-placa">Placa</label>
                    <input
                      id="rel-oe-placa"
                      type="text"
                      placeholder="Informe a placa"
                      value={filtros.placa ?? ''}
                      onChange={(e) => filtro('placa', e.target.value)}
                    />
                  </div>
                  <div className="form-field">
                    <label htmlFor="rel-oe-evento">Número do evento</label>
                    <input
                      id="rel-oe-evento"
                      type="text"
                      inputMode="numeric"
                      placeholder="Informe o número"
                      value={filtros.evento ?? ''}
                      onChange={(e) => filtro('evento', e.target.value.replace(/\D/g, ''))}
                    />
                  </div>
                  <div className="form-field">
                    <label htmlFor="rel-oe-situacao">Situação do veículo</label>
                    <select
                      id="rel-oe-situacao"
                      value={filtros.situacao ?? 'TODOS'}
                      onChange={(e) => filtro('situacao', e.target.value)}
                    >
                      <option value="TODOS">Todos</option>
                      <option value="NA_OFICINA">Na oficina</option>
                      <option value="SAIU">Já saiu</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="report-section">
                <div className="section-header">
                  <h3>Período</h3>
                  <span className="section-subtitle">
                    {modoId === 'VISITAS'
                      ? 'Filtra pela data informada da visita (não pela data em que ela chegou ao sistema)'
                      : 'Traz os veículos que estiveram na oficina externa em algum dia do período'}
                  </span>
                </div>
                <div className="filters-grid">
                  <div className="form-field">
                    <label htmlFor="rel-oe-de">De</label>
                    <input
                      id="rel-oe-de"
                      type="date"
                      value={filtros.de ?? ''}
                      onChange={(e) => filtro('de', e.target.value)}
                    />
                  </div>
                  <div className="form-field">
                    <label htmlFor="rel-oe-ate">Até</label>
                    <input
                      id="rel-oe-ate"
                      type="date"
                      value={filtros.ate ?? ''}
                      onChange={(e) => filtro('ate', e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="cars-report-modal-footer">
              <button type="button" className="btn-secondary" onClick={onFechar}>
                Fechar
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={gerar}
                disabled={!podeGerar}
                title={colunas.length === 0 ? 'Escolha ao menos uma coluna' : undefined}
              >
                {gerando ? 'Gerando...' : 'Gerar Relatório'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default RelatorioOficinaExternaModal;
